import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { DbService } from './db.service';
import { User } from '../models/user.model';
import { environment } from '../../environments/environment';

interface LoginResponse {
  token: string;
  refresh_token: string;
  user: User;
}

interface RefreshResponse {
  token: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);
  private db = inject(DbService);
  private router = inject(Router);

  readonly currentUser = signal<User | null>(null);
  readonly isAuthenticated = signal(false);

  private tokenCache: string | undefined | null = null; // null = not loaded yet
  private refreshPromise: Promise<string | null> | null = null;

  private parseJwt(token: string): Record<string, unknown> | null {
    try {
      const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      return JSON.parse(atob(base64));
    } catch {
      return null;
    }
  }

  isTokenValid(token: string): boolean {
    const payload = this.parseJwt(token);
    if (!payload || typeof payload['exp'] !== 'number') return false;
    return payload['exp'] * 1000 > Date.now();
  }

  async init(): Promise<void> {
    const token = await this.db.getToken();
    this.tokenCache = token;

    const activeToken = (token && this.isTokenValid(token)) ? token : await this.refresh();

    if (activeToken) {
      try {
        const user = await firstValueFrom(
          this.http.get<User>(`${environment.apiUrl}/users/me`)
        );
        this.currentUser.set(user);
        this.isAuthenticated.set(true);
      } catch {
        await this.db.clearToken();
        this.tokenCache = undefined;
      }
    } else if (token) {
      await this.db.clearToken();
      this.tokenCache = undefined;
    }
  }

  async login(email: string, password: string): Promise<void> {
    const res = await firstValueFrom(
      this.http.post<LoginResponse>(`${environment.apiUrl}/auth/login`, { email, password })
    );
    await this.db.setToken(res.token);
    this.tokenCache = res.token;
    if (res.refresh_token) {
      localStorage.setItem('refresh_token', res.refresh_token);
    }
    this.currentUser.set(res.user);
    this.isAuthenticated.set(true);
  }

  async refresh(): Promise<string | null> {
    if (this.refreshPromise) return this.refreshPromise;
    this.refreshPromise = this._doRefresh().finally(() => {
      this.refreshPromise = null;
    });
    return this.refreshPromise;
  }

  private async _doRefresh(): Promise<string | null> {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) return null;
    try {
      const res = await firstValueFrom(
        this.http.post<RefreshResponse>(`${environment.apiUrl}/auth/refresh`, {
          refresh_token: refreshToken,
        })
      );
      await this.db.setToken(res.token);
      this.tokenCache = res.token;
      return res.token;
    } catch {
      return null;
    }
  }

  async logout(): Promise<void> {
    await this.db.clearToken();
    this.tokenCache = undefined;
    localStorage.removeItem('refresh_token');
    this.currentUser.set(null);
    this.isAuthenticated.set(false);
    this.router.navigate(['/login']);
  }

  async getToken(): Promise<string | undefined> {
    if (this.tokenCache !== null) return this.tokenCache;
    this.tokenCache = await this.db.getToken();
    return this.tokenCache;
  }

  isAdmin(): boolean {
    return this.currentUser()?.role === 'admin';
  }
}
